import { Directive, Injectable, inject } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';

import type {
  ConfigPlugins,
  EditorButton,
  IProsemirrorConfig,
  JsonNode,
  PluginsButtons,
  PluginsEvents,
} from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BlockquotePlugin,
  BoldPlugin,
  ColorPlugin,
  ColumnsPlugin,
  HighlightPlugin,
  ContextMenuPlugin,
  EmojiEditorPlugin,
  HeadingPlugin,
  FullScreenPlugin,
  ImagePlugin,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  RtSchema,
  SCHEMAS,
  SizePlugin,
  StrikethroughPlugin,
  AlignExtension,
  AlignPlugin,
  UnderlinePlugin,
  VideoPlugin,
  HtmlClassPlugin,
  HtmlClassExtension,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';
import { WysiwygUploader } from '@core/wysiwyg-legacy';

import { AlignMenuService } from '../components/align-menu/align-menu.service';
import {
  PM_COLOR_SERVICE_FOR_TEXT_BACKGROUND,
  PM_COLOR_SERVICE_FOR_TEXT_COLOR,
} from '../components/color-menu/color-menu.service';
import { ColumnsMenuService } from '../components/columns-menu/columns-menu.service';
import { FullScreenService } from '../components/full-screen/full-screen.service';
import { HeadingMenuService } from '../components/heading-menu/heading-menu.service';
import { HtmlClassMenuService } from '../components/html-class-menu/html-class-menu.service';
import { ImageMenuService } from '../components/image/image-menu/image-menu.service';
import { ImageNodeComponent } from '../components/image/image-node/image-node.component';
import { ListMenuService } from '../components/list-menu/list-menu.service';
import { SizeMenuService } from '../components/size-menu/size-menu.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import {
  PM_CONTEXT_MENU_SERVICE_IMAGE,
  PM_CONTEXT_MENU_SERVICE_VIDEO,
} from '../components/tooltips/figure-context-menu/figure-context-menu.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';
import { VideoMenuService } from '../components/video/video-menu.service';
import { VideoNodeViewComponent } from '../components/video/video-node/video-node-view.component';
import { VideoParserService } from '../components/video/video-parser.service';
import { ProsemirrorConfig } from '../providers';

export type ProsemirrorRichConfigEvents = PluginsEvents<ConfigPlugins<ProsemirrorRichConfig>>;

const sanitizeJson = (node: JsonNode): JsonNode => {
  const attrs = { ...node.attrs };
  if (attrs.align !== undefined) {
    if (attrs.align === AlignExtension.attrs.align.default) {
      delete attrs.align;
    }
  }
  if (attrs['html-class'] !== undefined) {
    if (attrs['html-class'] === HtmlClassExtension.attrs['html-class'].default) {
      delete attrs['html-class'];
    }
  }

  return {
    ...node,
    attrs,
  };
};

@Injectable()
export class ProsemirrorRichConfig extends ProsemirrorBaseConfig implements IProsemirrorConfig {
  protected readonly headingMenuService = inject(HeadingMenuService, { optional: true });
  protected readonly sizeMenuService = inject(SizeMenuService, { optional: true });
  protected readonly alignMenuService = inject(AlignMenuService, { optional: true });
  protected readonly imageMenuService = inject(ImageMenuService, { optional: true });
  protected readonly listMenuService = inject(ListMenuService, { optional: true });
  protected readonly columnsMenuService = inject(ColumnsMenuService, { optional: true });
  protected readonly wysiwygUploader = inject(WysiwygUploader, { optional: true });
  protected readonly videoMenuService = inject(VideoMenuService, { optional: true });
  protected readonly videoParserService = inject(VideoParserService, { optional: true });
  protected readonly fullScreenService = inject(FullScreenService, { optional: true });
  protected readonly htmlClassMenuService = inject(HtmlClassMenuService, { optional: true });
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly contextMenuService? = inject(ContextToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });

  public eventStream$ = new Subject<ProsemirrorRichConfigEvents>();

  private scroll$ = this.scrollStream$ || EMPTY;

  private textColorColorMenuService = inject(PM_COLOR_SERVICE_FOR_TEXT_COLOR, { optional: true });
  private textBackgroundColorMenuService = inject(PM_COLOR_SERVICE_FOR_TEXT_BACKGROUND, {
    optional: true,
  });
  private imageContextMenuService = inject(PM_CONTEXT_MENU_SERVICE_IMAGE, { optional: true });
  private videoContextMenuService = inject(PM_CONTEXT_MENU_SERVICE_VIDEO, { optional: true });

  public getSchema() {
    return SCHEMAS[RtSchema.Rich](document, {});
  }

  public getRenderPlugins() {
    return [
      new ImagePlugin(
        this.prosemirrorComponentFactory(ImageNodeComponent),
        this.imageMenuService!,
        this.imageContextMenuService!,
        this.wysiwygUploader!,
        this.scroll$,
      ),
      new VideoPlugin(
        this.prosemirrorComponentFactory(VideoNodeViewComponent),
        this.videoMenuService!,
        this.videoContextMenuService!,
        this.videoParserService!,
        this.scroll$,
      ),
    ];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),

      new HeadingPlugin(this.headingMenuService!),
      new BlockquotePlugin(),
      new BoldPlugin(),
      new ItalicPlugin(),
      new StrikethroughPlugin(),
      new UnderlinePlugin(),
      new SizePlugin(this.sizeMenuService!),
      new ColorPlugin(this.textColorColorMenuService!),
      new HighlightPlugin(this.textBackgroundColorMenuService!),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new AutoLinkWrapPlugin(),
      new ListPlugin(this.listMenuService!),
      new AlignPlugin(this.alignMenuService!),
      new FullScreenPlugin(this.fullScreenService!),
      new HtmlClassPlugin(this.htmlClassMenuService!),
      new ColumnsPlugin(this.columnsMenuService!),
    ];
  }

  public registerToolbarButtons(buttons: PluginsButtons<ConfigPlugins<ProsemirrorRichConfig>>) {
    const ctxButtons = [
      buttons.toggleHeading,
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleLink,
    ];

    const leftButtons: EditorButton[][] = [
      [buttons.toggleHeading],
      [buttons.toggleBold, buttons.toggleItalic, buttons.toggleUnderline],
      [buttons.toggleSize, buttons.toggleHighlight, buttons.toggleColor, buttons.toggleBlockquote],
      [buttons.toggleAlign, buttons.toggleListMenu, buttons.toggleColumns],
      [buttons.toggleLink, buttons.toggleImage, buttons.toggleVideo],
      // [buttons.toggleFullScreen],
    ];

    const rightButtons: EditorButton[] = [buttons.toggleEmojiList];
    const moreButtons: EditorButton[] = [buttons.toggleHtmlClassMenu];

    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
    this.toolbarService!.setMoreButtons(moreButtons);
  }

  public sanitizeJSON(node: JsonNode): JsonNode {
    return {
      ...sanitizeJson(node),
      content: node.content.map(contentNode => sanitizeJson(contentNode)),
    };
  }
}

@Directive({
  selector: '[prosemirrorRichConfig]',
  providers: [
    ProsemirrorRichConfig,
    { provide: ProsemirrorConfig, useValue: ProsemirrorRichConfig },
  ],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorRichConfigDirective {}
